import { clsx } from 'clsx'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const GAP = 8

export function HoverPopover({
  trigger,
  children,
  align = 'left',
  width = 'w-72',
  triggerClassName = 'inline-flex cursor-default',
}: {
  trigger: ReactNode
  children: ReactNode
  align?: 'left' | 'right'
  width?: string
  /** Layout of the element that holds the trigger; a table cell that must truncate passes a shrinkable one. */
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [style, setStyle] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), 80)
  }

  useLayoutEffect(() => {
    if (!open) return
    const triggerEl = triggerRef.current
    const popoverEl = popoverRef.current
    if (!triggerEl || !popoverEl) return
    const triggerRect = triggerEl.getBoundingClientRect()
    const popoverRect = popoverEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let left =
      align === 'right'
        ? triggerRect.right - popoverRect.width
        : triggerRect.left
    left = Math.min(Math.max(left, GAP), vw - popoverRect.width - GAP)

    let top = triggerRect.bottom + GAP
    if (top + popoverRect.height > vh - GAP) {
      top = triggerRect.top - popoverRect.height - GAP
    }
    top = Math.max(top, GAP)

    setStyle({ top, left })
  }, [open, align])

  return (
    <>
      <span
        ref={triggerRef}
        className={triggerClassName}
        onMouseEnter={() => {
          cancelClose()
          setOpen(true)
        }}
        onMouseLeave={scheduleClose}
      >
        {trigger}
      </span>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            style={{
              position: 'fixed',
              top: style?.top ?? -9999,
              left: style?.left ?? -9999,
              visibility: style ? 'visible' : 'hidden',
            }}
            className={clsx(
              'z-[100] rounded-lg border border-border-default bg-raised p-3 shadow-xl',
              width,
            )}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  )
}
